import { Controller } from "@hotwired/stimulus"

// Modern file upload controller to replace jQuery FileUpload
export default class extends Controller {
  static targets = [
    "input", 
    "dropZone", 
    "uploadList", 
    "progressBar", 
    "errorMessage"
  ]
  
  static values = {
    url: String,
    maxFileSize: Number,
    maxFiles: Number,
    acceptedTypes: String,
    autoUpload: Boolean,
    propertyId: String,
    listingId: String,
    transactionId: String,
    fileParamName: String,
    extraParams: Object
  }

  connect() {
    console.log("FileUpload controller connected")
    this.setupDragAndDrop()
    this.setupInputHandler()
  }

  disconnect() {
    this.removeDragAndDropListeners()
  }

  // Setup drag and drop functionality
  setupDragAndDrop() {
    if (!this.hasDropZoneTarget) return

    this.boundHandlers = {
      dragover: this.handleDragOver.bind(this),
      dragleave: this.handleDragLeave.bind(this),
      drop: this.handleDrop.bind(this)
    }

    this.dropZoneTarget.addEventListener('dragover', this.boundHandlers.dragover)
    this.dropZoneTarget.addEventListener('dragleave', this.boundHandlers.dragleave)
    this.dropZoneTarget.addEventListener('drop', this.boundHandlers.drop)
  }

  removeDragAndDropListeners() {
    if (!this.hasDropZoneTarget || !this.boundHandlers) return

    this.dropZoneTarget.removeEventListener('dragover', this.boundHandlers.dragover)
    this.dropZoneTarget.removeEventListener('dragleave', this.boundHandlers.dragleave)
    this.dropZoneTarget.removeEventListener('drop', this.boundHandlers.drop)
  }

  setupInputHandler() {
    if (!this.hasInputTarget) return
    this.inputTarget.addEventListener('change', this.handleFileSelection.bind(this))
  }

  // Drag and drop event handlers
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add('drag-over')
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('drag-over')
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove('drag-over')
    
    const files = Array.from(event.dataTransfer.files)
    this.processFiles(files)
  }

  // File selection from input
  handleFileSelection(event) {
    const files = Array.from(event.target.files)
    this.processFiles(files)
  }

  // Process selected files
  async processFiles(files) {
    this.clearErrors()
    
    // Validate files
    const validFiles = this.validateFiles(files)
    if (validFiles.length === 0) return

    // Upload files
    if (this.autoUploadValue) {
      for (const file of validFiles) {
        await this.uploadFile(file)
      }
    } else {
      this.queueFiles(validFiles)
    }
  }

  // File validation
  validateFiles(files) {
    const validFiles = []
    const errors = []

    // Check max files limit
    if (this.maxFilesValue && files.length > this.maxFilesValue) {
      errors.push(`Maximum ${this.maxFilesValue} files allowed`)
      return []
    }

    files.forEach(file => {
      // Check file size
      if (this.maxFileSizeValue && file.size > this.maxFileSizeValue) {
        errors.push(`${file.name} is too large (max ${this.formatFileSize(this.maxFileSizeValue)})`)
        return
      }

      // Check file type
      if (this.acceptedTypesValue && !this.isValidFileType(file)) {
        errors.push(`${file.name} is not an allowed file type`)
        return
      }

      validFiles.push(file)
    })

    if (errors.length > 0) {
      this.showErrors(errors)
    }

    return validFiles
  }

  // Check if file type is valid
  isValidFileType(file) {
    if (!this.acceptedTypesValue) return true
    
    const acceptedTypes = this.acceptedTypesValue.split(',').map(type => type.trim())
    return acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      } else {
        return file.type.startsWith(type)
      }
    })
  }

  // Upload single file
  async uploadFile(file) {
    const uploadItem = this.createUploadItem(file)
    
    try {
      const formData = new FormData()
      
      // Use configurable parameter name, defaulting to 'docs[file][]'
      const fileParamName = this.fileParamNameValue || 'docs[file][]'
      formData.append(fileParamName, file)
      
      // Add Rails-specific params using direct associations
      if (this.propertyIdValue) {
        formData.append('property_id', this.propertyIdValue)
      } else if (this.listingIdValue) {
        formData.append('listing_id', this.listingIdValue)
      } else if (this.transactionIdValue) {
        formData.append('transaction_id', this.transactionIdValue)
      }
      
      // Add any extra parameters
      if (this.extraParamsValue) {
        Object.keys(this.extraParamsValue).forEach(key => {
          formData.append(key, this.extraParamsValue[key])
        })
      }

      console.log('Starting upload...')
      const xhr = await this.makeUploadRequest(formData, uploadItem)
      console.log('Upload response received:', xhr.status, xhr.statusText)
      
      // XMLHttpRequest uses status codes, not response.ok
      if (xhr.status >= 200 && xhr.status < 300) {
        console.log('Upload successful, calling success handler')
        this.handleUploadSuccess(uploadItem)
      } else {
        console.error('Upload failed with status:', xhr.status)
        throw new Error(`Upload failed: ${xhr.statusText}`)
      }
    } catch (error) {
      console.error('Upload error caught:', error)
      this.handleUploadError(uploadItem, error.message)
    }
  }

  // Make upload request with progress tracking
  makeUploadRequest(formData, uploadItem) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100
          this.updateProgress(uploadItem, percentComplete)
        }
      })

      xhr.addEventListener('load', () => {
        // Handle Turbo Stream responses
        if (xhr.status >= 200 && xhr.status < 300 && xhr.getResponseHeader('Content-Type')?.includes('turbo-stream')) {
          // Process Turbo Stream response
          const turboStream = xhr.responseText
          if (turboStream && window.Turbo) {
            // Create a temporary element to process the Turbo Stream
            const tempDiv = document.createElement('div')
            tempDiv.innerHTML = turboStream
            const turboStreamElements = tempDiv.querySelectorAll('turbo-stream')
            
            turboStreamElements.forEach(element => {
              window.Turbo.renderStreamMessage(element.outerHTML)
            })
          }
        }
        resolve(xhr)
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Network error'))
      })

      xhr.open('POST', this.urlValue)
      
      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        xhr.setRequestHeader('X-CSRF-Token', csrfToken)
      }
      
      // Request Turbo Stream format for partial updates
      xhr.setRequestHeader('Accept', 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml')

      xhr.send(formData)
    })
  }

  // Create upload item UI
  createUploadItem(file) {
    const uploadItem = document.createElement('div')
    uploadItem.className = 'upload-item'
    uploadItem.innerHTML = `
      <div class="upload-info">
        <div class="file-name">${file.name}</div>
        <div class="file-size">${this.formatFileSize(file.size)}</div>
      </div>
      <div class="upload-progress">
        <div class="progress">
          <div class="progress-bar progress-bar-success progress-bar-striped active" 
               role="progressbar" style="width: 0%"></div>
        </div>
      </div>
      <div class="upload-actions">
        <button type="button" class="btn btn-sm btn-warning cancel-upload">Cancel</button>
      </div>
    `

    // Add cancel functionality
    const cancelButton = uploadItem.querySelector('.cancel-upload')
    cancelButton.addEventListener('click', () => {
      uploadItem.remove()
    })

    if (this.hasUploadListTarget) {
      this.uploadListTarget.appendChild(uploadItem)
    }

    return uploadItem
  }

  // Update progress bar
  updateProgress(uploadItem, percent) {
    const progressBar = uploadItem.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.style.width = `${percent}%`
      progressBar.setAttribute('aria-valuenow', percent)
    }
  }

  // Handle successful upload
  handleUploadSuccess(uploadItem) {
    uploadItem.classList.add('upload-success')
    const progressBar = uploadItem.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.classList.remove('active', 'progress-bar-striped')
      progressBar.classList.add('progress-bar-success')
    }

    // Hide cancel button and show success
    const actions = uploadItem.querySelector('.upload-actions')
    if (actions) {
      actions.innerHTML = '<span class="text-success"><i class="fas fa-check"></i> Uploaded</span>'
    }

    // Remove the upload item after a brief delay to show success
    setTimeout(() => {
      uploadItem.remove()
    }, 2000) // 2 second delay to show success message

    // No need to reload page - Turbo Stream will handle document/image list updates
  }

  // Handle upload error
  handleUploadError(uploadItem, errorMessage) {
    uploadItem.classList.add('upload-error')
    const progressBar = uploadItem.querySelector('.progress-bar')
    if (progressBar) {
      progressBar.classList.remove('progress-bar-success', 'active', 'progress-bar-striped')
      progressBar.classList.add('progress-bar-danger')
    }

    const actions = uploadItem.querySelector('.upload-actions')
    if (actions) {
      actions.innerHTML = `<span class="text-danger"><i class="fas fa-exclamation-triangle"></i> ${errorMessage}</span>`
    }
  }

  // Queue files for manual upload
  queueFiles(files) {
    files.forEach(file => {
      const uploadItem = this.createUploadItem(file)
      uploadItem.dataset.file = JSON.stringify({
        name: file.name,
        size: file.size,
        type: file.type
      })
      
      // Add start upload button
      const actions = uploadItem.querySelector('.upload-actions')
      actions.innerHTML = `
        <button type="button" class="btn btn-sm btn-primary start-upload">Upload</button>
        <button type="button" class="btn btn-sm btn-warning cancel-upload">Cancel</button>
      `
      
      const startButton = actions.querySelector('.start-upload')
      startButton.addEventListener('click', () => {
        this.uploadFile(file)
      })
    })
  }

  // Utility methods
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  showErrors(errors) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.innerHTML = `
        <div class="alert alert-danger">
          <ul class="mb-0">
            ${errors.map(error => `<li>${error}</li>`).join('')}
          </ul>
        </div>
      `
    } else {
      console.error('File upload errors:', errors)
    }
  }

  clearErrors() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.innerHTML = ''
    }
  }

  // Manual upload trigger for queued files
  uploadAll() {
    const queuedItems = this.uploadListTarget.querySelectorAll('.upload-item:not(.upload-success):not(.upload-error)')
    queuedItems.forEach(item => {
      const startButton = item.querySelector('.start-upload')
      if (startButton) {
        startButton.click()
      }
    })
  }
}